import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { AuthCard } from "@/components/access/AuthCard";
import { AccessOfferCard } from "@/components/access/AccessOfferCard";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import {
  createCheckoutSession,
  fetchAccessCatalog,
  fetchCheckoutStatus,
  formatApiErrorDetail,
} from "@/lib/lumenApi";

export default function AccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, login, logout, refreshSession, register } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState("");

  useEffect(() => {
    let active = true;
    setCatalogLoading(true);

    fetchAccessCatalog()
      .then((data) => {
        if (active) {
          setCatalog(data);
        }
      })
      .finally(() => {
        if (active) {
          setCatalogLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      return undefined;
    }

    let cancelled = false;

    const poll = async (attempt = 0) => {
      if (cancelled || attempt >= 6) {
        return;
      }
      try {
        const transaction = await fetchCheckoutStatus(sessionId);
        if (transaction.payment_status === "paid") {
          toast.success("Le seuil a été franchi avec justesse.");
          await refreshSession();
          navigate("/entrer", { replace: true });
          return;
        }
        if (transaction.status === "expired") {
          toast.error("La session de paiement a expiré.");
          navigate("/entrer", { replace: true });
          return;
        }
      } catch (error) {
        toast.error(error.message || "Le retour de paiement reste silencieux.");
        return;
      }

      window.setTimeout(() => poll(attempt + 1), 2000);
    };

    poll();

    return () => {
      cancelled = true;
    };
  }, [location.search, navigate, refreshSession]);

  const currentEntitlements = useMemo(() => {
    if (!user) {
      return { cycle: false, integral: false };
    }
    return user.entitlements || { cycle: false, integral: false };
  }, [user]);

  const handleAuth = async (handler, payload) => {
    try {
      const nextUser = await handler(payload);
      toast.success("Le compte de passage est désormais ouvert.");
      return nextUser;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  };

  const handlePurchase = async (offerId) => {
    if (!user) {
      toast.error("Ouvre d’abord un compte de passage pour lier l’achat à ton accès.");
      return;
    }

    setCheckoutBusy(offerId);
    try {
      const session = await createCheckoutSession({
        package_id: offerId,
        origin_url: window.location.origin,
        return_path: "/entrer",
      });
      window.location.href = session.url;
    } catch (error) {
      toast.error(error.message || "Le passage de paiement reste silencieux pour l’instant.");
      setCheckoutBusy("");
    }
  };

  return (
    <div className="lumen-page access-page" id="top">
      <div className="lumen-shell pb-20">
        <header className="lumen-header fade-rise" data-testid="access-header">
          <Link className="lumen-brand" to="/" data-testid="access-brand-link">
            <BrandMark wrapperClassName="lumen-sigil" testId="access-brand-sigil" imageTestId="access-brand-sigil-image" />
            <span data-testid="access-brand-name">LUMEN</span>
          </Link>

          <div className="flex flex-wrap items-center gap-3" data-testid="access-header-actions">
            <Link className="lumen-nav-link" to="/parcours" data-testid="access-header-journey-link">
              Parcours
            </Link>
            <Link className="lumen-nav-link" to="/bibliotheque" data-testid="access-header-library-link">
              Bibliothèque
            </Link>
          </div>
        </header>

        <main className="space-y-0" data-testid="access-main">
          <section className="lumen-scroll-stage stage-manifest access-hero-stage" data-testid="access-hero-section">
            <div className="lumen-frame value-major access-hero-shell">
              <div className="space-y-5" data-testid="access-hero-copy">
                <p className="lumen-kicker" data-testid="access-hero-kicker">
                  Passage
                </p>
                <h1 className="lumen-title text-5xl sm:text-6xl lg:text-7xl" data-testid="access-hero-title">
                  Entrer sans banaliser le sanctuaire.
                </h1>
                <p className="lumen-copy max-w-3xl text-base md:text-lg" data-testid="access-hero-copy-text">
                  Deux seuils réels : le Cycle I complet, puis le corpus vivant actuellement ouvert.
                </p>
              </div>

              <div className="access-hero-proof" data-testid="access-hero-proof">
                <div className="access-proof-item" data-testid="access-proof-open-lessons">
                  <span className="lumen-panel-kicker">Chambres ouvertes</span>
                  <span>L’effet miroir • Ce qui dépend de toi</span>
                </div>
                <div className="access-proof-item" data-testid="access-proof-structure">
                  <span className="lumen-panel-kicker">Corpus visible</span>
                  <span>Cycle I • Strate II • Bibliothèque</span>
                </div>
              </div>
            </div>
          </section>

          <section className="lumen-scroll-stage stage-threshold access-offers-stage" data-testid="access-offers-section">
            <div className="access-offers-grid" data-testid="access-offers-grid">
              {catalog?.offers?.map((offer, index) => (
                <AccessOfferCard
                  key={offer.id}
                  active={Boolean(currentEntitlements[offer.tier])}
                  dataPrefix={`access-offer-${offer.id}`}
                  disabled={checkoutBusy === offer.id || catalogLoading}
                  isPrimary={index === 0}
                  offer={offer}
                  onPurchase={handlePurchase}
                />
              ))}
            </div>
          </section>

          <section className="lumen-scroll-stage stage-access access-account-stage" data-testid="access-account-section">
            <div className="access-account-grid" data-testid="access-account-grid">
              <AuthCard
                loading={loading}
                onLogin={(payload) => handleAuth(login, payload)}
                onLogout={async () => {
                  await logout();
                  toast.success("Le passage a été refermé proprement.");
                }}
                onRegister={(payload) => handleAuth(register, payload)}
                user={user && user !== false ? user : null}
              />

              <section className="lumen-card value-fragment access-status-card" data-testid="access-status-card">
                <div className="access-status-shell">
                  <div className="space-y-3">
                    <div className="lumen-panel-topline" data-testid="access-status-topline">
                      <p className="lumen-panel-kicker" data-testid="access-status-kicker">
                        État d’accès
                      </p>
                      <span className="lumen-plus">+</span>
                    </div>
                    <p className="access-status-intro" data-testid="access-status-title">
                      Relevé discret du passage.
                    </p>
                  </div>

                  <div className="access-status-grid" data-testid="access-status-grid">
                    <div className="access-status-item" data-testid="access-status-cycle">
                      <span className="lumen-panel-kicker">Cycle I</span>
                      <span>{currentEntitlements.cycle ? "Actif" : "En attente"}</span>
                    </div>
                    <div className="access-status-item" data-testid="access-status-integral">
                      <span className="lumen-panel-kicker">Intégral</span>
                      <span>{currentEntitlements.integral ? "Actif" : "En attente"}</span>
                    </div>
                  </div>

                  <div className="access-status-links" data-testid="access-status-actions">
                    <Link className="lumen-nav-link access-status-link" to="/parcours" data-testid="access-status-journey-button">
                      Carte du cycle
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link className="lumen-nav-link access-status-link" to="/" data-testid="access-status-home-button">
                      Sanctuaire
                      <ArrowLeft className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <section className="lumen-scroll-stage stage-fragment access-future-stage" data-testid="access-future-section">
            <div className="lumen-frame value-fragment access-future-shell">
              <p className="lumen-panel-kicker" data-testid="access-future-kicker">
                Réserve
              </p>
              <p className="access-future-note" data-testid="access-future-title">
                {catalog?.future_offer?.title} — {catalog?.future_offer?.price_label}
              </p>
              <p className="lumen-copy" data-testid="access-future-copy">
                {catalog?.future_offer?.summary}
              </p>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}